import React, { useRef } from 'react';
import {
  Paper,
  Box,
  Typography,
  useTheme,
  alpha,
  IconButton
} from '@mui/material';
import {
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon
} from '@mui/icons-material';

interface BannerPreviewProps {
  imageUrl?: string | null;
  onUpload: (file: File) => void;
  onRemove: () => void;
  acceptFileTypes?: string;
  maxFileSizeMB?: number;
  height?: number | string;
}

const BannerPreview: React.FC<BannerPreviewProps> = ({ 
  imageUrl, 
  onUpload,
  onRemove, 
  acceptFileTypes = ".jpg,.jpeg,.png",
  maxFileSizeMB = 2,
  height = 120 
}) => {
  const theme = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      const acceptedTypes = acceptFileTypes.split(',').map(type => type.trim());
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      if (!acceptedTypes.includes(fileExtension)) {
        alert(`Please select a file with one of these formats: ${acceptFileTypes}`);
        return;
      }
      
      // Check file size
      if (file.size > maxFileSizeMB * 1024 * 1024) {
        alert(`File size must be less than ${maxFileSizeMB}MB`);
        return;
      }
      
      onUpload(file);
      
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClickUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: alpha(theme.palette.secondary.light, 0.05),
        border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
        height: '100%',
        position: 'relative'
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
        {imageUrl ? <VisibilityIcon fontSize="small" /> : <ImageIcon fontSize="small" />} 
        {imageUrl ? 'Live Preview' : 'Banner Preview'}
      </Typography>

      <Box
        sx={{
          height,
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: imageUrl ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
          border: imageUrl 
            ? `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
            : `2px dashed ${alpha(theme.palette.secondary.main, 0.3)}`,
          backgroundColor: imageUrl 
            ? 'transparent' 
            : alpha(theme.palette.secondary.light, 0.1),
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: theme.palette.secondary.main,
            backgroundColor: alpha(theme.palette.secondary.light, 0.05)
          }
        }}
        onClick={handleClickUpload}
      >
        {imageUrl ? (
          <>
            {/* Banner Image */}
            <Box
              component="img"
              src={imageUrl}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
              alt="Banner preview"
            />
            
            {/* Clear preview button */}
            <IconButton
              size="small"
              onClick={handleRemoveClick}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: alpha(theme.palette.common.white, 0.9),
                '&:hover': {
                  backgroundColor: theme.palette.common.white
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            
            {/* Preview overlay with dimensions */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                backgroundColor: alpha(theme.palette.common.black, 0.7),
                color: theme.palette.common.white,
                padding: '4px 8px',
                fontSize: '0.7rem',
                display: 'flex',
                justifyContent: 'space-between'
              }}
            >
              <span>E-commerce Banner</span>
              <span>1200×400px</span>
            </Box>
          </>
        ) : (
          // Upload prompt
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              color: 'text.secondary'
            }}
          >
            <UploadIcon fontSize="large" />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Click to upload banner
            </Typography>
            <Typography variant="caption" sx={{ mt: 0.5, textAlign: 'center' }}>
              JPG, PNG (Max {maxFileSizeMB}MB)
            </Typography>
          </Box>
        )}
        
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={acceptFileTypes}
          style={{ display: 'none' }}
        />
      </Box>
      
      {/* Preview info */}
      {imageUrl && (
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
            ✓ Image successfully uploaded
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Click on image to replace
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default BannerPreview;